/**
 * Reading a bundle back (decision 7; part 24b, tables B, C, T′, I′ and O′): its archive opened with the bundle's limits
 * (part 17, L3), its manifest read (`readManifest`) and checked against itself (table C), then every file it names read,
 * decoded and checked, piece by piece in table O′'s order. {@link checkBundle} is the first run: every check, nothing kept
 * but the counts. {@link readBundle} is the second: the same checks, each piece handed on as it is read, and anything
 * that differs from the first run is `file-changed` (principle 2). Nothing here trusts the file (principle 1).
 */
import { ArchiveError, BUNDLE_ARCHIVE, isStripped, openZip, readImageHeader, toHex } from '@cockatrice/core';
import type { ArchiveProblem, ByteSource, ZipArchive } from '@cockatrice/core';
import { MAX_RECORD_LENGTH } from '@cockatrice/layout';
import { isId } from '@cockatrice/storage';
import type { StoreName } from '@cockatrice/storage';
import type { JsonValue } from './json.ts';
import { BUNDLE_FORMAT, BUNDLE_VERSION, readManifest } from './manifest.ts';
import type { AnyManifest, ManifestChapter, ManifestImageRecord, ManifestNoteRecord, ManifestShare, ManifestShareLink, TextFile, V7Store } from './manifest.ts';
import { MANIFEST_FILE } from './names.ts';
import type { BundlePiece, BundleVersion, ImageState } from './pieces.ts';
import { encodeText } from './text.ts';

/** Table B: why a bundle isn't read, besides the archive's own problems. */
export type BundleProblem = 'not-bundle' | 'newer' | 'damaged' | 'file-changed';

/** Every bundle problem, in table B's order. */
export const BUNDLE_PROBLEMS: readonly BundleProblem[] = Object.freeze(['not-bundle', 'newer', 'damaged', 'file-changed']);

/** Thrown for a bundle table B refuses; `problem` says why. */
export class BundleError extends Error {
  override readonly name = 'BundleError';
  readonly problem: BundleProblem;

  constructor(problem: BundleProblem, message: string) {
    super(message);
    this.problem = problem;
  }
}

/** MB4: what a bundle holds, counted by the first run. */
export interface BundleCounts {
  readonly chapters: number;
  readonly trash: number;
  readonly copies: number;
  readonly snapshots: number;
  readonly kept: number;
  readonly found: number;
  readonly missing: number;
  readonly notes: number;
  readonly comments: number;
  readonly refused: number;
  /** Files of the archive the manifest doesn't name (X1): never read. */
  readonly ignored: number;
}

/** Every count, in MB4's order. */
export const BUNDLE_COUNTS: readonly (keyof BundleCounts)[] = Object.freeze(['chapters', 'trash', 'copies', 'snapshots', 'kept', 'found', 'missing', 'notes', 'comments', 'refused', 'ignored']);

/** MB4: a bundle checked whole (the first run): what the review needs, and the manifest's SHA-256 the second run names. */
export interface BundleCheck {
  readonly version: BundleVersion;
  /** The SHA-256 of `manifest.json`, 64 lowercase hex digits. */
  readonly manifest: string;
  readonly book: string;
  /** The book's metadata as stored (part 05's serialised form, untrusted), or null. */
  readonly metadata: string | null;
  readonly counts: BundleCounts;
}

/** What a run needs beside the file. */
export interface UnpackOptions {
  /** SHA-256 (WebCrypto's in the worker). */
  readonly digest: (bytes: Uint8Array) => Promise<Uint8Array>;
  /** Hears the compressed bytes consumed as each file is read (part 17, R12). */
  readonly onRead?: (bytes: number) => void;
}

/** B10: the archive's problems that, in a second run, mean the file changed since the first passed. */
const CHANGED_ARCHIVE: readonly ArchiveProblem[] = ['damaged', 'not-zip', 'bad-name', 'too-many-files', 'too-big', 'unsupported'];

/** A chapter of any version, its notes empty in versions 5 to 8 (MB1, MB2). */
export type BundleChapter = ManifestChapter & { readonly notes: string };

/** The manifest of any version, with an earlier version's missing lists empty (principle 6). */
export interface BundleManifest {
  readonly version: BundleVersion;
  readonly manifest: AnyManifest;
  readonly chapters: readonly BundleChapter[];
  readonly trash: readonly (BundleChapter & { readonly trashed: { readonly at: number; readonly device: string } })[];
  readonly images: Extract<AnyManifest, { version: 2 | 3 | 4 | 5 | 6 | 7 | 8 }>['images'];
  /** Versions 3 and 4's character and place notes. */
  readonly bookNotes: Extract<AnyManifest, { version: 3 | 4 }>['bookNotes'];
  readonly comments: Extract<AnyManifest, { version: 4 }>['comments'];
  /**
   * Versions 5 to 8's note records (part 19e), version 5's with no link (part 38d), version 5 to 7's with no share part
   * (part 38e, BV5).
   */
  readonly notes: readonly ManifestNoteRecord[];
  /** Versions 6 to 8's reading links (part 38d). */
  readonly shareLinks: readonly ManifestShareLink[];
  /** Versions 7 and 8's shares (part 38b, BU), version 7's with no role keys (part 38e, BV5). */
  readonly shares: readonly ManifestShare[];
  readonly refused: readonly { readonly store: V7Store; readonly key: string | null; readonly raw: JsonValue; readonly text: TextFile | null; readonly bytes: string | null }[];
}

/** Principle 6, BV5: `manifest`'s note records as version 8 has them: an earlier version's with no link or share part. */
function notesOf(manifest: AnyManifest): readonly ManifestNoteRecord[] {
  if (manifest.version === 8) return manifest.notes;
  if (manifest.version === 7 || manifest.version === 6) return manifest.notes.map((note) => ({ ...note, shared: null }));
  return manifest.version === 5 ? manifest.notes.map((note) => ({ ...note, link: null, shared: null })) : [];
}

/** Principle 6: `manifest`'s lists, every version's. */
export function normaliseManifest(manifest: AnyManifest): BundleManifest {
  const refused = manifest.version === 1 ? manifest.unreadable.map((record) => ({ ...record, bytes: null })) : manifest.unreadable;
  const records = manifest.version === 5 || manifest.version === 6 || manifest.version === 7 || manifest.version === 8;
  return {
    version: manifest.version,
    manifest,
    chapters: records ? manifest.chapters.map((chapter) => ({ ...chapter, notes: '' })) : manifest.chapters,
    trash: records ? manifest.trash.map((chapter) => ({ ...chapter, notes: '' })) : manifest.trash,
    images: manifest.version === 1 ? [] : manifest.images,
    bookNotes: manifest.version === 3 || manifest.version === 4 ? manifest.bookNotes : [],
    comments: manifest.version === 4 ? manifest.comments : [],
    notes: notesOf(manifest),
    shareLinks: manifest.version === 8 || manifest.version === 7 || manifest.version === 6 ? manifest.shareLinks : [],
    // BV5: a version 7 share holds no role keys.
    shares: manifest.version === 8 ? manifest.shares : manifest.version === 7 ? manifest.shares.map((share) => ({ ...share, roleKeys: [] })) : [],
    refused,
  };
}

/** B3–B5: the manifest in `text`, or why not. */
function manifestOf(text: string): BundleManifest {
  try {
    return normaliseManifest(readManifest(text));
  } catch (error) {
    // Deep-fix r2: the refusal's class from the value itself, never from where the reader stopped (a top-level field of
    // our own manifest is named by the empty path too). Parsed again only on this path, so a good bundle is parsed once.
    let value: unknown;
    try {
      value = JSON.parse(text);
    } catch {
      throw new BundleError('not-bundle', 'a manifest that isn’t JSON');
    }
    // B3: an object, of our format.
    if (typeof value !== 'object' || value === null || Array.isArray(value) || Reflect.get(value, 'format') !== BUNDLE_FORMAT) throw new BundleError('not-bundle', 'another format');
    // B4.
    const version: unknown = Reflect.get(value, 'version');
    if (Number.isSafeInteger(version) && Number(version) > BUNDLE_VERSION) throw new BundleError('newer', `format version ${String(version)}`);
    // B5.
    throw new BundleError('damaged', String(error));
  }
}

/** Table C's claims: each (store, key) pair at most once. */
class Claims {
  readonly #keys = new Map<StoreName | V7Store, Set<string>>();

  claim(store: StoreName | V7Store, key: string): void {
    const keys = this.#keys.get(store) ?? new Set<string>();
    if (keys.has(key)) throw new BundleError('damaged', `${store} ${key.slice(0, 64)} twice`);
    keys.add(key);
    this.#keys.set(store, keys);
  }
}

/** C1–C15: `read` against itself; returns every file it names (C13: each once). */
export function checkManifest(read: BundleManifest): ReadonlySet<string> {
  const { manifest } = read;
  const claims = new Claims();
  const files = new Set<string>();
  const name = (file: string | null): void => {
    if (file === null) return;
    if (files.has(file)) throw new BundleError('damaged', `${file} named twice`);
    files.add(file);
  };
  const { book } = manifest;
  // C1, C2: one book record, or none.
  if ((book.metadata === null) !== (book.theme === null) || (book.metadata === null) !== (book.createdAt === null)) throw new BundleError('damaged', 'half a book record');
  if (book.metadata !== null) claims.claim('books', book.id);
  name(book.metadata?.file ?? null);
  name(book.theme?.file ?? null);
  // C3, C6: the chapters a snapshot may be of.
  const chapters = new Set<string>();
  for (const chapter of [...read.chapters, ...read.trash]) {
    claims.claim('entries', chapter.id);
    if (chapter.text !== null) claims.claim('chapters', chapter.id);
    // MB7 (part 19e): a chapter's notes become the note under its ID.
    if (chapter.notes !== '') claims.claim('notes', chapter.id);
    name(chapter.text?.file ?? null);
    chapters.add(chapter.id);
  }
  for (const copy of manifest.conflictCopies) {
    claims.claim('conflictCopies', copy.id);
    name(copy.text.file);
    chapters.add(copy.chapterId);
  }
  for (const record of read.refused) {
    // C10–C12: a refused record's key, the text its key names, the binary it held.
    if (record.key !== null) claims.claim(record.store, record.key);
    if (record.store === 'entries' && isId(record.key)) chapters.add(record.key);
    if (record.text !== null) {
      if ((record.store !== 'entries' && record.store !== 'snapshots') || !isId(record.key)) throw new BundleError('damaged', `a text for a refused ${record.store} record`);
      claims.claim(record.store === 'entries' ? 'chapters' : 'snapshotTexts', record.key);
    }
    if (record.bytes !== null && record.store !== 'imageBytes') throw new BundleError('damaged', `a binary for a refused ${record.store} record`);
    name(record.text?.file ?? null);
    name(record.bytes);
  }
  for (const snapshot of manifest.snapshots) {
    // C5, C6.
    claims.claim('snapshots', snapshot.id);
    if (snapshot.text !== null) claims.claim('snapshotTexts', snapshot.id);
    if (!chapters.has(snapshot.chapterId)) throw new BundleError('damaged', `a snapshot of no chapter ${snapshot.chapterId}`);
    name(snapshot.text?.file ?? null);
  }
  for (const image of read.images) {
    // C7.
    if (image.record === null && image.file === null) throw new BundleError('damaged', `image ${image.id} holds nothing`);
    if (image.record !== null) claims.claim('images', image.id);
    if (image.file !== null) claims.claim('imageBytes', image.id);
    name(image.file);
  }
  // C8, C9, MB7, V5-6 (part 19e): the book notes, comments and note records each become a note under its ID, so they
  // claim one namespace.
  for (const note of [...read.bookNotes, ...read.comments, ...read.notes]) {
    claims.claim('notes', note.id);
    name(note.text.file);
  }
  // V6-1 (part 38d): a reading link claims its ID beside a refused one's.
  for (const link of read.shareLinks) claims.claim('shareLinks', link.id);
  // BU (part 38b): a share claims its ID beside a refused one's.
  for (const share of read.shares) claims.claim('shares', share.id);
  return files;
}

/** Whether `a` and `b` hold the same bytes. */
function same(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((byte, index) => byte === b[index]);
}

/** T′1, T′2: strict UTF-8, a leading byte-order mark dropped. */
const UTF8 = new TextDecoder('utf-8', { fatal: true });

/** T′1–T′5: the text `bytes` hold, as `text` names them; throws B8 for one table T′ refuses. */
export function decodeText(text: TextFile, bytes: Uint8Array): string {
  if (text.exact !== null) {
    if (!same(encodeText(text.exact).bytes, bytes)) throw new BundleError('damaged', `${text.file} isn’t its exact text`);
    return text.exact;
  }
  try {
    return UTF8.decode(bytes);
  } catch {
    throw new BundleError('damaged', `${text.file} isn’t UTF-8`);
  }
}

/** Table I′ (I′1): whether `bytes`, whose SHA-256 is `sha256`, are the image `record` describes, as the store keeps one. */
export function imageAgrees(record: ManifestImageRecord, bytes: Uint8Array, sha256: string): boolean {
  if (sha256 !== record.sha256 || bytes.length !== record.size) return false;
  const header = readImageHeader(bytes);
  return typeof header !== 'string' && header.format === record.format && header.width === record.width && header.height === record.height && isStripped(bytes, record.format);
}

/** One run over an opened bundle: reads each file once (C13), so the progress never passes the file's size. */
class Run {
  readonly #archive: ZipArchive;
  readonly #options: UnpackOptions;

  constructor(archive: ZipArchive, options: UnpackOptions) {
    this.#archive = archive;
    this.#options = options;
  }

  bytes(file: string): Promise<Uint8Array> {
    return this.#archive.read(file, this.#options.onRead);
  }

  async text(text: TextFile): Promise<string> {
    return decodeText(text, await this.bytes(text.file));
  }

  async textOrNull(text: TextFile | null): Promise<string | null> {
    return text === null ? null : this.text(text);
  }

  async sha256(bytes: Uint8Array): Promise<string> {
    return toHex(await this.#options.digest(bytes));
  }
}

/** I′1–I′4: an image's state, from its record and its bytes. */
async function imageState(run: Run, record: ManifestImageRecord | null, bytes: Uint8Array | null): Promise<ImageState> {
  if (bytes === null) return 'missing';
  return record !== null && imageAgrees(record, bytes, await run.sha256(bytes)) ? 'kept' : 'found';
}

/**
 * Tables B, C, T′, I′, O′: reads the bundle in `archive`, handing each piece to `take` in order; `expected`, in a second
 * run, the first run's manifest SHA-256 (B9). Returns what the bundle holds.
 */
async function unpack(archive: ZipArchive, options: UnpackOptions, expected: string | null, take: (piece: BundlePiece) => Promise<void>): Promise<BundleCheck> {
  const run = new Run(archive, options);
  // B2.
  const paths = new Set(archive.files.map((file) => file.path));
  if (!paths.has(MANIFEST_FILE)) throw new BundleError('not-bundle', 'no manifest');
  const manifestBytes = await run.bytes(MANIFEST_FILE);
  const sha256 = await run.sha256(manifestBytes);
  // B9.
  if (expected !== null && sha256 !== expected) throw new BundleError('file-changed', 'another manifest');
  let manifestText: string;
  try {
    manifestText = UTF8.decode(manifestBytes);
  } catch {
    throw new BundleError('not-bundle', 'a manifest that isn’t UTF-8');
  }
  const read = manifestOf(manifestText);
  const named = checkManifest(read);
  // B7: every file named is in the archive, before any is read.
  for (const file of named) if (!paths.has(file)) throw new BundleError('damaged', `${file} missing`);
  const { manifest } = read;
  const counts = { kept: 0, found: 0, missing: 0 };
  const metadata = await run.textOrNull(manifest.book.metadata);
  await take({ type: 'book', id: manifest.book.id, createdAt: manifest.book.createdAt, metadata, theme: await run.textOrNull(manifest.book.theme), device: manifest.device, version: read.version });
  for (const chapter of read.chapters) {
    await take({ type: 'chapter', id: chapter.id, title: chapter.title, position: chapter.position, synopsis: chapter.synopsis, notes: chapter.notes, revision: chapter.revision, text: await run.textOrNull(chapter.text), trashed: null });
  }
  for (const chapter of read.trash) {
    const trashed = { at: chapter.trashed.at, device: chapter.trashed.device };
    await take({ type: 'chapter', id: chapter.id, title: chapter.title, position: chapter.position, synopsis: chapter.synopsis, notes: chapter.notes, revision: chapter.revision, text: await run.textOrNull(chapter.text), trashed });
  }
  for (const copy of manifest.conflictCopies) await take({ type: 'copy', id: copy.id, chapterId: copy.chapterId, createdAt: copy.createdAt, origin: copy.origin, text: await run.text(copy.text) });
  for (const snapshot of manifest.snapshots) {
    const { id, chapterId, owner, seq, createdAt, settled, zone, reason, pins } = snapshot;
    await take({ type: 'snapshot', id, chapterId, owner, seq, createdAt, settled, zone, reason, pins, text: await run.textOrNull(snapshot.text) });
  }
  for (const image of read.images) {
    const bytes = image.file === null ? null : await run.bytes(image.file);
    const state = await imageState(run, image.record, bytes);
    counts[state] += 1;
    await take({ type: 'image', id: image.id, record: image.record, bytes, state });
  }
  for (const note of read.bookNotes) await take({ type: 'bookNote', id: note.id, kind: note.kind, name: note.name, text: await run.text(note.text), createdAt: note.createdAt });
  for (const comment of read.comments) {
    const { id, chapterId, sourceKey, origin, author, createdAt, storedAt, range, quote, before, after } = comment;
    await take({ type: 'comment', id, chapterId, sourceKey, origin, author, createdAt, storedAt, range, quote, before, after, text: await run.text(comment.text) });
  }
  for (const { text: file, ...note } of read.notes) {
    const text = await run.text(file);
    // V5-6, NR6: a tombstone keeps no words.
    if (note.deletedAt !== null && text !== '') throw new BundleError('damaged', `${file.file} holds a deleted note’s words`);
    await take({ type: 'note', ...note, text });
  }
  for (const link of read.shareLinks) await take({ type: 'shareLink', ...link });
  for (const share of read.shares) await take({ type: 'share', ...share });
  for (const record of read.refused) {
    const text = await run.textOrNull(record.text);
    // I′5: the binary as it is, unchecked.
    const bytes = record.bytes === null ? null : await run.bytes(record.bytes);
    await take({ type: 'refused', store: record.store, key: record.key, raw: record.raw, text, bytes });
  }
  return {
    version: read.version,
    manifest: sha256,
    book: manifest.book.id,
    // Fix-next 85 (the security audit of 2026-11-02, run 2): what the page is handed for the review's title is never
    // longer than a record its reader takes; a longer one is no title (the book's record goes as P11's refused).
    metadata: metadata !== null && metadata.length > MAX_RECORD_LENGTH ? null : metadata,
    counts: {
      chapters: manifest.chapters.length,
      trash: manifest.trash.length,
      copies: manifest.conflictCopies.length,
      snapshots: manifest.snapshots.length,
      ...counts,
      notes: read.bookNotes.length + read.notes.filter((note) => note.deletedAt === null).length,
      comments: read.comments.length,
      refused: read.refused.length,
      ignored: paths.size - named.size - 1,
    },
  };
}

/** The bundle archive in `source`, opened with the bundle's limits (B1). */
function open(source: ByteSource): Promise<ZipArchive> {
  return openZip(source, BUNDLE_ARCHIVE);
}

/** The first run (principle 2): the bundle in `source` read and checked whole; nothing kept but what the review needs. */
export async function checkBundle(source: ByteSource, options: UnpackOptions): Promise<BundleCheck> {
  return unpack(await open(source), options, null, () => Promise.resolve());
}

/**
 * The second run (principle 2): the bundle in `source` read again with every check, each piece handed to `take` in
 * table O′'s order, the next read only once `take` settles; `manifest` is the first run's SHA-256 of the manifest. A
 * refusal the first run didn't give means the file changed: `file-changed` (B9, B10), but `unreadable` (B11).
 */
export async function readBundle(source: ByteSource, manifest: string, options: UnpackOptions, take: (piece: BundlePiece) => Promise<void>): Promise<void> {
  try {
    await unpack(await open(source), options, manifest, take);
  } catch (error) {
    if (error instanceof BundleError || (error instanceof ArchiveError && CHANGED_ARCHIVE.includes(error.problem))) throw new BundleError('file-changed', error.message);
    throw error;
  }
}
